import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface MapleMarketNavGameProps {
  className?: string;
  height?: number;
}

interface GhostState {
  id: number;
  x: number;
  color: string;
  speed: number;
  direction: 1 | -1;
  dead: boolean;
  chasing: boolean;
}

const GHOST_COLORS = ['#FF0000', '#FFB8FF', '#00FFFF', '#FFB852'];
const DEFAULT_NAV_GAME_HEIGHT = 56;
const SPRITE_SIZE = 32;
const PAC_SPEED = 2.56;
const AUTO_SPEED = 1.28;
const CATCH_DISTANCE = 20;
const DEAD_COLOR = '#ffffff';
const START_PAC_X = 120;
const IDLE_TIMEOUT_MS = 5000;

const Ghost = ({ color, facingLeft }: { color: string; facingLeft: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={SPRITE_SIZE}
    height={SPRITE_SIZE}
    style={{ opacity: color === DEAD_COLOR ? 0.4 : 1 }}
  >
    <path
      fill={color}
      d="M20,10v11c0,0.55-0.45,1-1,1c-0.79,0-1.51-0.31-2.14-0.58C16.35,21.2,15.88,21,15.5,21s-0.85,0.2-1.36,0.42C13.51,21.69,12.79,22,12,22s-1.51-0.31-2.14-0.58C9.35,21.2,8.88,21,8.5,21s-0.85,0.2-1.36,0.42C6.51,21.69,5.79,22,5,22c-0.55,0-1-0.45-1-1V10c0-4.41,3.59-8,8-8S20,5.59,20,10z"
    />
    <circle cx={facingLeft ? '14' : '15'} cy="10" r="1.2" fill="#fff" />
    <circle cx={facingLeft ? '8' : '9'} cy="10" r="1.2" fill="#fff" />
    <circle cx={facingLeft ? '13.5' : '15.5'} cy="10" r="0.5" fill="#1a1a2e" />
    <circle cx={facingLeft ? '7.5' : '9.5'} cy="10" r="0.5" fill="#1a1a2e" />
  </svg>
);

const makeGhosts = (): GhostState[] =>
  GHOST_COLORS.map((color, index) => ({
    id: index,
    x: 220 + index * 120,
    color,
    speed: 0.77 + Math.random() * 0.96,
    direction: (Math.random() > 0.5 ? 1 : -1) as 1 | -1,
    dead: false,
    chasing: index % 2 === 0,
  }));

export default function MapleMarketNavGame({ className, height = DEFAULT_NAV_GAME_HEIGHT }: MapleMarketNavGameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const keysRef = useRef<Set<string>>(new Set());
  const lastInputAtRef = useRef<number>(Date.now());
  const [playerControlled, setPlayerControlled] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const [pacX, setPacX] = useState(START_PAC_X);
  const [pacDir, setPacDir] = useState<1 | -1>(1);
  const [ghosts, setGhosts] = useState<GhostState[]>(makeGhosts);
  const [winMessage, setWinMessage] = useState(false);
  const [score, setScore] = useState(0);

  const getWidth = useCallback(() => containerRef.current?.offsetWidth ?? 900, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        return;
      }

      event.preventDefault();
      keysRef.current.add(event.key);
      lastInputAtRef.current = Date.now();
      setIsIdle(false);
      if (!playerControlled) {
        setPlayerControlled(true);
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      keysRef.current.delete(event.key);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [playerControlled]);

  useEffect(() => {
    if (isIdle) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (Date.now() - lastInputAtRef.current >= IDLE_TIMEOUT_MS) {
        keysRef.current.clear();
        setPlayerControlled(false);
        setIsIdle(true);
      }
    }, 250);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isIdle]);

  useEffect(() => {
    let frameId = 0;

    const loop = () => {
      if (isIdle) {
        frameId = window.requestAnimationFrame(loop);
        return;
      }

      const width = getWidth();

      setPacX((previousPacX) => {
        if (playerControlled) {
          let nextPacX = previousPacX;

          if (keysRef.current.has('ArrowLeft')) {
            nextPacX -= PAC_SPEED;
            setPacDir(-1);
          }

          if (keysRef.current.has('ArrowRight')) {
            nextPacX += PAC_SPEED;
            setPacDir(1);
          }

          return Math.max(-SPRITE_SIZE, Math.min(width + SPRITE_SIZE, nextPacX));
        }

        setPacDir((currentDir) => {
          if (previousPacX <= 0) {
            return 1;
          }
          if (previousPacX >= width - SPRITE_SIZE) {
            return -1;
          }
          return currentDir;
        });

        return previousPacX + pacDir * AUTO_SPEED;
      });

      setGhosts((previousGhosts) =>
        previousGhosts.map((ghost) => {
          if (ghost.dead) {
            return ghost;
          }

          let nextDirection = ghost.direction;
          let nextChasing = ghost.chasing;

          if (Math.random() < 0.002) {
            nextChasing = !nextChasing;
          }

          if (playerControlled) {
            nextDirection = nextChasing
              ? pacX > ghost.x ? 1 : -1
              : pacX > ghost.x ? -1 : 1;
          } else {
            if (ghost.x <= -SPRITE_SIZE) {
              nextDirection = 1;
            }
            if (ghost.x >= width + SPRITE_SIZE) {
              nextDirection = -1;
            }
            if (Math.random() < 0.005) {
              nextDirection = (nextDirection * -1) as 1 | -1;
            }
          }

          const speed = nextChasing ? ghost.speed * 1.3 : ghost.speed;
          let nextX = ghost.x + nextDirection * speed;

          if (nextX < -SPRITE_SIZE * 2) {
            nextX = width + SPRITE_SIZE;
          }
          if (nextX > width + SPRITE_SIZE * 2) {
            nextX = -SPRITE_SIZE;
          }

          return {
            ...ghost,
            x: nextX,
            direction: nextDirection,
            chasing: nextChasing,
          };
        })
      );

      if (playerControlled) {
        setGhosts((previousGhosts) => {
          let ateGhost = false;
          let pacDied = false;

          const pacCenterX = pacX + SPRITE_SIZE / 2;

          const updatedGhosts = previousGhosts.map((ghost) => {
            if (ghost.dead) {
              return ghost;
            }

            const ghostCenterX = ghost.x + SPRITE_SIZE / 2;
            const distance = Math.abs(pacCenterX - ghostCenterX);
            const ghostIsOnMouthSide = pacDir === 1
              ? ghostCenterX >= pacCenterX
              : ghostCenterX <= pacCenterX;
            const ghostIsOnBackSide = !ghostIsOnMouthSide;

            if (distance < CATCH_DISTANCE && ghostIsOnMouthSide) {
              ateGhost = true;
              return {
                ...ghost,
                dead: true,
                color: DEAD_COLOR,
              };
            }

            if (distance < CATCH_DISTANCE && ghostIsOnBackSide) {
              pacDied = true;
            }

            return ghost;
          });

          if (pacDied) {
            keysRef.current.clear();
            setPlayerControlled(false);
            setIsIdle(false);
            setPacX(START_PAC_X);
            setPacDir(1);
            setScore(0);
            setWinMessage(false);
            lastInputAtRef.current = Date.now();
            return makeGhosts();
          }

          if (ateGhost) {
            setScore((currentScore) => currentScore + 100);
          }

          const allGhostsDead = updatedGhosts.every((ghost) => ghost.dead);
          const hadLiveGhostBefore = previousGhosts.some((ghost) => !ghost.dead);

          if (allGhostsDead && hadLiveGhostBefore) {
            setWinMessage(true);
            window.setTimeout(() => {
              setGhosts(makeGhosts());
              setWinMessage(false);
              lastInputAtRef.current = Date.now();
            }, 2000);
          }

          return updatedGhosts;
        });
      }

      frameId = window.requestAnimationFrame(loop);
    };

    frameId = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(frameId);
  }, [getWidth, isIdle, pacDir, pacX, playerControlled]);

  return (
    <div
      ref={containerRef}
      className={cn('absolute inset-0 overflow-hidden select-none pointer-events-none', className)}
      style={{ height }}
      aria-hidden="true"
    >
      <div className="absolute inset-0 flex items-center gap-6 px-4">
        {Array.from({ length: 40 }).map((_, index) => (
          <div
            key={index}
            className="h-1.5 w-1.5 shrink-0 rounded-full opacity-30"
            style={{ backgroundColor: '#c59d32' }}
          />
        ))}
      </div>

      {ghosts.map((ghost) => (
        <div
          key={ghost.id}
          className="absolute top-1/2 -translate-y-1/2 transition-opacity duration-300"
          style={{ left: ghost.x, opacity: ghost.dead ? 0.3 : 1 }}
        >
          <Ghost color={ghost.dead ? DEAD_COLOR : ghost.color} facingLeft={ghost.direction === -1} />
        </div>
      ))}

      <div className="absolute top-1/2 -translate-y-1/2" style={{ left: pacX }}>
        <img
          src="/os/assets/icons/maple-market-pacman.svg"
          alt=""
          aria-hidden="true"
          width={SPRITE_SIZE}
          height={SPRITE_SIZE}
          draggable={false}
          style={{ transform: pacDir === -1 ? 'scaleX(-1)' : 'none' }}
        />
      </div>

      {playerControlled && (
        <div className="absolute right-24 top-1/2 -translate-y-1/2">
          <span
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: '#ffd700', textShadow: '0 0 6px rgba(255,215,0,0.4)' }}
          >
            {score.toString().padStart(4, '0')}
          </span>
        </div>
      )}

      {(winMessage || !playerControlled || isIdle) && (
        <div className="absolute inset-0 flex items-center px-3">
          <span
            className="text-xs font-bold uppercase tracking-[0.28em]"
            style={{
              color: winMessage ? '#ffd700' : '#8a8a6e',
              textShadow: winMessage ? '0 0 10px #ffd700' : '0 1px 2px rgba(0,0,0,0.8)',
            }}
          >
            {winMessage ? 'CLEAR!' : '← → TO PLAY'}
          </span>
        </div>
      )}
    </div>
  );
}